import "./footer.css";
import { state } from "../store";
import { useSnapshot } from "valtio";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useEffect, useRef, useState } from "react";
import AudioButton from "../AudioButton/AudioButton";

export default function Footer() {
  const { showLeva, zoomedInBlackClock, zoomedInWhiteClock, zoomedInBackWallsClock, finishedMainLoading, finishedIntro, hideInstruction, hoveredModel, hoveredElementName, openedPanel, viewType, isDraggingCamera } = useSnapshot(state);
  const boxRef = useRef(null);
  const hoveredModelRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Track mouse movement to update hovered model position
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Close mobile menu when camera is dragging or panel opens
  useEffect(() => {
    if (isDraggingCamera) {
      setIsMobileMenuOpen(false);
    }
  }, [isDraggingCamera]);

  // Close mobile menu when a panel opens
  useEffect(() => {
    if (openedPanel) {
      setIsMobileMenuOpen(false);
    }
  }, [openedPanel]);

  // Handle ESC key to close panels or toggle mobile menu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        // If any panel is open, close it first
        if (openedPanel) {
          state.openedPanel = null;
        } else {
          // Only toggle mobile menu if no panel is open
          setIsMobileMenuOpen((prev) => !prev);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [openedPanel]);

  // Hamburger menu icon component
  const HamburgerIcon = ({ isOpen }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="hamburger-icon">
      <path d="M3 6H21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`hamburger-line line-1 ${isOpen ? "open" : ""}`} />
      <path d="M3 12H21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`hamburger-line line-2 ${isOpen ? "open" : ""}`} />
      <path d="M3 18H21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`hamburger-line line-3 ${isOpen ? "open" : ""}`} />
    </svg>
  );

  // Animate hovered model visibility with fade effect
  useGSAP(() => {
    if (hoveredModelRef.current) {
      if (hoveredModel) {
        gsap.to(hoveredModelRef.current, {
          opacity: 1,
          duration: 0.3,
          ease: "power2.out",
        });
      } else {
        gsap.to(hoveredModelRef.current, {
          opacity: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    }
  }, [hoveredModel]);

  // useEffect(() => {
  //   if (finishedIntro) {
  //     boxRef.current.classList.add("show");
  //   }
  // }, [finishedIntro]);

  return (
    <>
      {/* Hovered model tooltip - rendered outside footer wrapper to avoid container constraints */}
      <div
        ref={hoveredModelRef}
        className="footer-hovered-model"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          pointerEvents: "none",
          opacity: hoveredModel ? 1 : 0,
          zIndex: 10000,
          transform: `translate3d(${mousePosition.x + 15}px, ${mousePosition.y - 30}px, 0)`,
        }}
      >
        {hoveredElementName}
      </div>

      {/* Background overlay for better menu readability */}
      {finishedIntro && <div className={`footer-menu-background ${isMobileMenuOpen ? "show" : ""}`}></div>}

      {/* Invisible overlay to close menu when clicked outside */}
      {finishedIntro && isMobileMenuOpen && <div className="footer-menu-close-overlay" onClick={() => setIsMobileMenuOpen(false)}></div>}

      <div className="footer-wrapper">
        <div className={`footer-input-container ${!zoomedInBlackClock && !zoomedInWhiteClock && !zoomedInBackWallsClock && finishedMainLoading && finishedIntro ? "show" : ""}`}>
          {/* Mobile hamburger menu button */}
          <div className="mobile-menu-button-container">
            <div
              className={`mobile-menu-button ${openedPanel || isDraggingCamera ? "hidden" : ""}`}
              onClick={() => {
                if (!openedPanel && !isDraggingCamera) {
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                }
              }}
            >
              <HamburgerIcon isOpen={isMobileMenuOpen} />
              <span className="menu-text">{isMobileMenuOpen ? "CLOSE" : "MENU"}</span>
            </div>
            <div className={`audio-button-container ${openedPanel || isDraggingCamera ? "hidden" : ""}`}>
              <AudioButton url="/music.mp3" startAudio={true} />
            </div>
          </div>

          {/* Desktop menu - hidden on mobile */}
          <div className={`footer-main-menu desktop-menu ${openedPanel || isDraggingCamera ? "hidden" : ""}`}>
            <div
              className="footer-options-container"
              onClick={() => {
                if (!openedPanel && !isDraggingCamera) {
                  state.openedPanel = "environment";
                }
              }}
            >
              OPTIONS
            </div>
            <div
              className="footer-options-container"
              onClick={() => {
                if (!openedPanel && !isDraggingCamera) {
                  state.openedPanel = "presets";
                }
              }}
            >
              PRESETS
            </div>
            <div
              className={`footer-options-container ${viewType === "main" ? "active" : ""}`}
              onClick={() => {
                if (!openedPanel && !isDraggingCamera) {
                  state.viewType = "main";
                }
              }}
            >
              TOP
            </div>
            <div
              className={`footer-options-container ${viewType === "deck" ? "active" : ""}`}
              onClick={() => {
                if (!openedPanel && !isDraggingCamera) {
                  state.viewType = "deck";
                }
              }}
            >
              DECK
            </div>
            <div
              className={`footer-options-container ${viewType === "bow" ? "active" : ""}`}
              onClick={() => {
                if (!openedPanel && !isDraggingCamera) {
                  state.viewType = "bow";
                }
              }}
            >
              BOW
            </div>
            <div
              className={`footer-options-container ${viewType === "cabin" ? "active" : ""}`}
              onClick={() => {
                if (!openedPanel && !isDraggingCamera) {
                  state.viewType = "cabin";
                }
              }}
            >
              CABIN
            </div>
          </div>

          {/* Mobile menu - appears vertically when hamburger is clicked */}
          <div className={`mobile-menu-options ${isMobileMenuOpen ? "open" : ""}`}>
            <div
              className="footer-options-container mobile-option"
              onClick={() => {
                if (!isDraggingCamera) {
                  state.openedPanel = "environment";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              ENVIRONMENT
            </div>
            <div
              className="footer-options-container mobile-option"
              onClick={() => {
                if (!isDraggingCamera) {
                  state.openedPanel = "presets";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              PRESET COLORS
            </div>
            <div
              className={`footer-options-container mobile-option ${viewType === "main" ? "active" : ""}`}
              onClick={() => {
                if (!isDraggingCamera) {
                  state.viewType = "main";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              TOP VIEW
            </div>
            <div
              className={`footer-options-container mobile-option ${viewType === "deck" ? "active" : ""}`}
              onClick={() => {
                if (!isDraggingCamera) {
                  state.viewType = "deck";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              DECK VIEW
            </div>
            <div
              className={`footer-options-container mobile-option ${viewType === "bow" ? "active" : ""}`}
              onClick={() => {
                if (!isDraggingCamera) {
                  state.viewType = "bow";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              BOW VIEW
            </div>
            <div
              className={`footer-options-container mobile-option ${viewType === "cabin" ? "active" : ""}`}
              onClick={() => {
                if (!isDraggingCamera) {
                  state.viewType = "cabin";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              CABIN VIEW
            </div>
            <div
              className="footer-options-container mobile-option"
              onClick={() => {
                if (!isDraggingCamera) {
                  state.openedPanel = "deck-colors";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              DECK COLORS
            </div>
            <div
              className="footer-options-container mobile-option"
              onClick={() => {
                if (!isDraggingCamera) {
                  state.openedPanel = "accent-colors";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              ACCENT COLORS
            </div>
            <div
              className="footer-options-container mobile-option"
              onClick={() => {
                if (!isDraggingCamera) {
                  state.openedPanel = "hull-colors";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              BODY COLORS
            </div>
            <div
              className="footer-options-container mobile-option"
              onClick={() => {
                if (!isDraggingCamera) {
                  state.openedPanel = "seats-colors";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              SEATS COLORS
            </div>
            <div
              className="footer-options-container mobile-option"
              onClick={() => {
                if (!isDraggingCamera) {
                  state.openedPanel = "hull-colors";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              HULL COLORS
            </div>
            <div
              className="footer-options-container mobile-option"
              onClick={() => {
                if (!isDraggingCamera) {
                  state.openedPanel = "config";
                  setIsMobileMenuOpen(false);
                }
              }}
            >
              QUALITY
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
